import type { FC, ReactElement, ReactNode } from 'react';
import { Children, isValidElement } from 'react';

/**
 * Inspired by SolidJS Show component
 * https://docs.solidjs.com/references/api-reference/control-flow/Show
 */

interface ShowProps<T = unknown> {
  when: T | undefined | null | false;
  children: ReactNode | ((value: T) => ReactNode);
}

interface ShowFirstMatchingProps {
  children: ReactElement<ShowProps>[] | ReactElement<ShowProps>;
  fallback?: ReactNode;
}

export function Show<T>({ when, children }: ShowProps<T>): ReactElement | null {
  if (!when) return null;
  return <>{typeof children === 'function' ? children(when) : children}</>;
}

export const ShowFirstMatching: FC<ShowFirstMatchingProps> = ({ children, fallback = null }) => {
  'use memo';

  let matchFound = false;

  const mappedChildren = Children.map(children, (child) => {
    if (isValidElement(child) && child.props.when && !matchFound) {
      matchFound = true;
      return child;
    }
    return null;
  });

  const renderChildren = { matched: matchFound, children: mappedChildren };

  return <>{renderChildren.matched ? renderChildren.children : fallback}</>;
};
